import { useMediaQuery } from "@mui/material";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { convertGptSummary } from "../../../../functions/job";
import { formatSalary } from "../../../../helpers/conditional_rendering";
import { getCompanyLogoImage } from "../../../../helpers/data_management";
import { sendTrackingEvent } from "../../../../helpers/tracking_management";
import {
  CompanyNameRow,
  CompanyVerifiedBadge,
} from "../../AccordionJobCard/styles";
import {
  BoxContainer,
  CheckboxHover,
  CheckboxIcon,
  CheckboxStyled,
  CheckboxWrapper,
  CheckedBox,
  UncheckedBox,
} from "../styles";
import {
  CardWrapper,
  ChipContainer,
  CompanyImage,
  CompanyNameStyled,
  DescriptionContainer,
  DetailChip,
  HeaderImage,
  JobSummaryContainer,
  JobSummaryText,
  JobSummaryTextBold,
  JobTitleStyled,
  MoreDetailButton,
  ShowMoreText,
  SummaryBulletItem,
  SummaryBulletList,
  TitleContainer,
} from "./styles";

export default function SimilarJobCard(props) {
  const {
    index,
    job,
    setInView,
    checkedJob,
    handleSelectJob,
    dialog,
    smallCard,
    currentTab,
    origin,
    similarJobsTriggered,
    postApplyImpressionOrigin,
  } = props;

  const isShort = useMediaQuery("(max-height: 720px)");
  const { t, i18n } = useTranslation();
  const isBahasa =
    (i18n?.language || "en").split("-")[0].toLowerCase() === "ms";

  const getJobTypeLabel = (jobTypeValue) => {
    if (!jobTypeValue) return "";
    const key = jobTypeValue
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companydetail.job_type_labels.${key}`, {
      defaultValue: jobTypeValue,
    });
  };

  const getStateRegionLabel = (stateRegionValue) => {
    if (!stateRegionValue) return "";
    const key = stateRegionValue
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companies.state_region_labels.${key}`, {
      defaultValue: stateRegionValue,
    });
  };

  const [hover, setHover] = useState(false);
  const [showFullSkills, setShowFullSkills] = useState(false);
  const [showFullBenefits, setShowFullBenefits] = useState(false);

  const [ref, entry] = useIntersectionObserver({
    threshold: 1,
  });

  const router = useRouter();
  const fullUrl = router?.asPath;

  const convertedGptSummary = useMemo(
    () => convertGptSummary(job, { preferBahasa: isBahasa }),
    [job, isBahasa]
  );

  const truncatedSkills = useMemo(() => {
    if (!job?.skills?.length) return "";

    let skillsString = job.skills.map((skill) => skill.name).join(", ");
    if (skillsString.length <= 70) return skillsString;

    let truncated = skillsString.slice(0, 70).trim();
    let lastCommaIndex = truncated.lastIndexOf(",");
    if (lastCommaIndex > 0) {
      truncated = truncated.slice(0, lastCommaIndex);
    }

    const remainingCount = job.skills.length - truncated.split(",").length;

    return showFullSkills ? (
      skillsString
    ) : (
      <>
        {truncated}...{" "}
        <ShowMoreText onClick={() => setShowFullSkills(true)}>
          +{remainingCount} {t("jobs.more")}
        </ShowMoreText>
      </>
    );
  }, [job?.skills, showFullSkills, t]);

  const truncatedBenefits = useMemo(() => {
    if (!job?.company?.benefits?.length) return "";

    let benefitsString = job?.company?.benefits
      ?.map((benefit) => benefit?.title)
      .join(", ");
    if (benefitsString.length <= 70) return benefitsString;

    let truncated = benefitsString.slice(0, 70).trim();
    let lastCommaIndex = truncated.lastIndexOf(",");
    if (lastCommaIndex > 0) {
      truncated = truncated.slice(0, lastCommaIndex);
    }

    const remainingCount =
      job?.company?.benefits.length - truncated.split(",").length;

    return showFullBenefits ? (
      benefitsString
    ) : (
      <>
        {truncated}...{" "}
        <ShowMoreText onClick={() => setShowFullBenefits(true)}>
          +{remainingCount} {t("jobs.more")}
        </ShowMoreText>
      </>
    );
  }, [job?.company?.benefits, showFullBenefits, t]);

  const company =
    job?.crossedBorder &&
    job?.crossBorderData?.source_country !=
      process.env.NEXT_PUBLIC_JSW_GEOLOCATION.toUpperCase()
      ? job?.crossBorderData?.company
      : job?.company;

  const companyLogo = useMemo(() => {
    return getCompanyLogoImage(company);
  }, [company]);

  //TRACKING RELATED
  // function trackImpressionInView() {
  //   // POSTHOG JOB IMPRESSION TRACKING
  //   sendTrackingEvent({
  //     event: "CE_job-impression",
  //     origin: "job_application_popup",
  //     job_discovery: "similar_jobs",
  //   });
  // }

  const salaryRange = useMemo(() => {
    if (job?.salary === "Undisclosed" || job?.salary === "Undefined")
      return t("jobs.salary_undisclosed");

    return formatSalary(job?.salary, job?.crossBorderData);
  }, [job?.salary, job?.crossBorderData, t]);

  const jobTypeLabel = useMemo(
    () => getJobTypeLabel(job?.jobType),
    [job?.jobType, t]
  );
  const stateRegionLabel = useMemo(
    () => getStateRegionLabel(job?.stateRegion),
    [job?.stateRegion, t]
  );

  const trackingTimeout = useRef(null);

  useEffect(() => {
    if ((dialog && entry?.isIntersecting) || entry?.isIntersecting) {
      setInView(index);

      if (trackingTimeout.current) {
        clearTimeout(trackingTimeout.current);
      }

      trackingTimeout.current = setTimeout(() => {
        trackImpressionInView(job, index);
      }, 250);
    } else {
      if (trackingTimeout.current) {
        clearTimeout(trackingTimeout.current);
        trackingTimeout.current = null;
      }
    }

    // Cleanup function
    return () => {
      if (trackingTimeout.current) {
        clearTimeout(trackingTimeout.current);
      }
    };
  }, [entry?.isIntersecting]);

  // Track job impression for bulk apply
  function trackImpressionInView(job, index) {
    let impressionOrigin;

    if (postApplyImpressionOrigin) {
      impressionOrigin = postApplyImpressionOrigin;
    } else if (fullUrl.includes("job-application-history")) {
      impressionOrigin =
        origin === "Applied"
          ? "profile_application_applied"
          : "profile_application_recommend";
    } else if (fullUrl === "/jobs") {
      impressionOrigin =
        currentTab === "job-list" ? "job_listing_page" : "fyp_page";
    } else if (fullUrl.includes("saved-jobs-&-companies")) {
      impressionOrigin =
        currentTab === "saved-jobs"
          ? "profile_saved_jobs"
          : "profile_saved_recommend";
    } else {
      impressionOrigin =
        currentTab == "internship-bulk"
          ? "internship_bank_page"
          : "job_detail_page";
    }

    const currentJobDiscovery =
      currentTab == "internship-bulk"
        ? "internship_bank_page"
        : similarJobsTriggered
        ? "view_similar_jobs"
        : "post_apply_rec";

    sendTrackingEvent({
      event: "CE_job-impression",
      "job-id": job?.id,
      origin: impressionOrigin,
      job_discovery: currentJobDiscovery,
    });
  }

  const isCompanyVerified =
    company?.verified === true ||
    company?.name === "Hiredly" ||
    company?.name === "Hiredly X";

  return (
    <CardWrapper
      $dialog={dialog}
      $smallCard={smallCard}
      selected={checkedJob(job)}
      ref={ref}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => {
        handleSelectJob(job);
      }}
    >
      {!smallCard && !isShort ? (
        <HeaderImage src={companyLogo} placeholder={companyLogo} />
      ) : null}

      <TitleContainer>
        <BoxContainer
          justifyContent={"space-between"}
          alignItems={"flex-start"}
          gap={"0.25rem"}
        >
          <JobTitleStyled>{job?.title}</JobTitleStyled>
          <CheckboxWrapper>
            <CheckboxStyled
              icon={<UncheckedBox />}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectJob(job);
              }}
              checked={checkedJob(job)}
              checkedIcon={
                <CheckedBox>
                  <CheckboxIcon />
                </CheckedBox>
              }
            />
            <CheckboxHover>
              <CheckboxIcon hover={hover} selected={checkedJob(job)} />
            </CheckboxHover>
          </CheckboxWrapper>
        </BoxContainer>
        <BoxContainer alignItems={"center"} gap={"0.5rem"} sx={{ minWidth: 0 }}>
          <CompanyImage src={companyLogo} />
          <CompanyNameRow>
            <CompanyNameStyled>{company?.name}</CompanyNameStyled>
            {isCompanyVerified && (
              <CompanyVerifiedBadge aria-label="Verified company" />
            )}
          </CompanyNameRow>
        </BoxContainer>
      </TitleContainer>

      {!smallCard ? (
        <DescriptionContainer>
          <ChipContainer>
            <DetailChip type={"jobType"}>{jobTypeLabel}</DetailChip>
            <DetailChip type={"state"}>{stateRegionLabel}</DetailChip>
            <DetailChip type={"salary"}>{salaryRange}</DetailChip>
          </ChipContainer>

          <>
            <JobSummaryContainer type={"summary"}>
              <JobSummaryTextBold>
                {t("jobs.for_you_card.ai_generated_summary")}
              </JobSummaryTextBold>
              {convertedGptSummary.length > 0 && (
                <JobSummaryText
                  component={
                    Array.isArray(convertedGptSummary) ? "div" : undefined
                  }
                >
                  {Array.isArray(convertedGptSummary) ? (
                    <SummaryBulletList>
                      {convertedGptSummary.map((line, index) => (
                        <SummaryBulletItem key={index}>
                          {line}
                        </SummaryBulletItem>
                      ))}
                    </SummaryBulletList>
                  ) : (
                    convertedGptSummary
                  )}
                </JobSummaryText>
              )}
            </JobSummaryContainer>

            {job?.company?.benefits?.length > 0 && (
              <JobSummaryContainer type={"benefits"}>
                <b>{t("jobs.for_you_card.benefits_label")} </b>
                {truncatedBenefits}
              </JobSummaryContainer>
            )}
            {job?.skills?.length > 0 && (
              <JobSummaryContainer type={"skills"}>
                <b>{t("jobs.for_you_card.skills_label")} </b>
                {truncatedSkills}
              </JobSummaryContainer>
            )}
          </>
        </DescriptionContainer>
      ) : null}

      <MoreDetailButton
        href={"/jobs/" + job?.slug + `?origin=similar_jobs`}
        onClick={() => {
          if (dialog) {
            Cookies.set("click-source", "job_application_popup");
          }
        }}
        target={"_blank"}
      >
        {t("jobs.for_you_card.view_full_details")}
      </MoreDetailButton>
    </CardWrapper>
  );
}
